import React, { useState, useEffect, useRef } from 'react';
import userImage from './IMG/User.png';
import lgLogo from './IMG/LG Logo.svg';
import ewLogo from './IMG/EW Logo.png';
import rajsnehagroLogo from './IMG/RajSnehAgro.png';

function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTables: 0,
    tables: [],
    databases: [],
    loading: true,
    error: null
  });
  
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [], loading: false, error: null });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [slipDetail, setSlipDetail] = useState({ data: null, images: [], loading: false, error: null });
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [customDbConfig, setCustomDbConfig] = useState({
    host: '',
    username: '',
    password: '',
    dbname: ''
  });
  const tableDetailsRef = useRef(null);

  useEffect(() => {
    fetchDatabaseList();
  }, []);

  // Load current user from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('lbUser');
      if (stored) {
        const userObj = JSON.parse(stored);
        setCurrentUser(userObj);
        console.log('Loaded user from localStorage:', userObj);
        if (!userObj.companyId) {
          console.warn('companyId is missing in user object:', userObj);
        } else {
          console.log('companyId found:', userObj.companyId);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (selectedDatabase) {
      fetchDashboardStats(selectedDatabase);
    }
  }, [selectedDatabase]);

  useEffect(() => {
    // Auto scroll to the inline table details when a table is selected and content is present
    if (selectedTable && tableDetailsRef.current && !tableData.loading) {
      try {
        tableDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        // no-op if scrolling not supported
      }
    }
  }, [selectedTable, tableData.loading]);

  const fetchDatabaseList = async () => {
    try {
      console.log('Fetching database list...');
      const response = await fetch('https://electroweighing.com/list-databases.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Database list response:', data);
      
      if (data.success) {
        console.log('Databases found:', data.databases);
        setStats(prev => ({
          ...prev,
          databases: data.databases || [],
          loading: false,
          error: null
        }));
        // Auto-select first database
        if (data.databases && data.databases.length > 0) {
          console.log('Auto-selecting database:', data.databases[0]);
          setSelectedDatabase(data.databases[0]);
        }
      } else {
        console.error('Database list error:', data.error);
        setStats(prev => ({ ...prev, loading: false, error: data.error }));
      }
    } catch (error) {
      console.error('Database list fetch error:', error);
      setStats(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const fetchDashboardStats = async (dbName) => {
    try {
      // Get companyId from currentUser if available
      const companyId = currentUser && currentUser.companyId ? currentUser.companyId : '';
      let url = `https://electroweighing.com/dashboard.php?database=${encodeURIComponent(dbName)}`;
      if (companyId) {
        url += `&companyId=${encodeURIComponent(companyId)}`;
      }
      console.log('Fetching dashboard stats from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Dashboard stats response:', data);

      if (data.success) {
        console.log('Total tables:', data.totalTables, 'Tables array:', data.tables);
        setStats(prev => ({
          ...prev,
          totalUsers: data.totalUsers || 0,
          totalTables: data.totalTables || 0,
          tables: data.tables || [],
          loading: false,
          error: null
        }));
      } else {
        console.error('Dashboard stats error:', data.error);
        setStats(prev => ({ ...prev, loading: false, error: data.error }));
      }
    } catch (error) {
      console.error('Dashboard stats fetch error:', error);
      setStats(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const handleConnectCustomDatabase = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://electroweighing.com/connect-database.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customDbConfig)
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Connected to custom database successfully!');
        setShowConnectForm(false);
        fetchDatabaseList(); // Refresh database list
      } else {
        alert(`Connection failed: ${data.error}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleTableClick = async (tableName) => {
    setSelectedTable(tableName);
    setTableData({ columns: [], rows: [], loading: true, error: null });
    setSelectedRowId(null);
    setSlipDetail({ data: null, images: [], loading: false, error: null });
    
    try {
      // Use optimized list endpoint that excludes blobs and returns summary columns
      const response = await fetch(`https://electroweighing.com/get-slip-list.php?database=${encodeURIComponent(selectedDatabase)}&table=${encodeURIComponent(tableName)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setTableData({
          columns: data.columns || [],
          rows: data.rows || [],
          totalRows: data.totalRows || 0,
          displayedRows: data.displayedRows || 0,
          loading: false,
          error: null
        });
      } else {
        setTableData({ columns: [], rows: [], loading: false, error: data.error });
      }
    } catch (error) {
      setTableData({ columns: [], rows: [], loading: false, error: error.message });
    }
  };

  const openSlipDetail = async (row) => {
    const id = row.Id || row.ID || row.id;
    if (!id) return;
    setSelectedRowId(id);
    setSlipDetail({ data: null, images: [], loading: true, error: null });
    try {
      const url = `https://electroweighing.com/get-slip-detail.php?database=${encodeURIComponent(selectedDatabase)}&table=${encodeURIComponent(selectedTable)}&id=${encodeURIComponent(id)}`;
      const resp = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }});
      if (!resp.ok) throw new Error(`Server error: ${resp.status}`);
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Failed to load detail');
      setSlipDetail({ data: data.data || null, images: data.images || [], loading: false, error: null });
    } catch (e) {
      setSlipDetail({ data: null, images: [], loading: false, error: e.message });
    }
  };

  const closeTableView = () => {
    setSelectedTable(null);
    setTableData({ columns: [], rows: [], loading: false, error: null });
  };

  const handleExport = (format) => {
    console.log('Export button clicked, format:', format);
    console.log('Selected table:', selectedTable);
    console.log('Selected database:', selectedDatabase);
    
    if (!selectedTable || !selectedDatabase) {
      alert('No table selected');
      return;
    }
    
    // Build URL with filter parameters
    let exportUrl = format === 'excel' 
      ? `https://electroweighing.com/export-excel.php?database=${encodeURIComponent(selectedDatabase)}&table=${encodeURIComponent(selectedTable)}`
      : `https://electroweighing.com/export-pdf.php?database=${encodeURIComponent(selectedDatabase)}&table=${encodeURIComponent(selectedTable)}`;
    
    // Add filter parameters if they exist
    if (fromDate) {
      exportUrl += `&fromDate=${encodeURIComponent(fromDate)}`;
    }
    if (toDate) {
      exportUrl += `&toDate=${encodeURIComponent(toDate)}`;
    }
    if (searchText) {
      exportUrl += `&search=${encodeURIComponent(searchText)}`;
    }
    if (sortColumn) {
      exportUrl += `&sortColumn=${encodeURIComponent(sortColumn)}&sortOrder=${encodeURIComponent(sortOrder)}`;
    }
    
    // Add user details for export footer
    if (currentUser) {
      if (currentUser.name) {
        exportUrl += `&userName=${encodeURIComponent(currentUser.name)}`;
      }
      if (currentUser.company) {
        exportUrl += `&userCompany=${encodeURIComponent(currentUser.company)}`;
      }
    }
    
    console.log('Export URL:', exportUrl);
    
    try {
      // Method 1: Try using fetch to download as blob
      fetch(exportUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Download failed');
          }
          return response.blob();
        })
        .then(blob => {
          // Create a download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `${selectedTable}_export.${format === 'excel' ? 'xls' : 'pdf'}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          console.log('Download triggered successfully');
          alert('Download started! Check your downloads folder.');
        })
        .catch(error => {
          console.error('Fetch download failed:', error);
          // Fallback: Open in new window
          window.open(exportUrl, '_blank');
          alert('Opening download in new tab...');
        });
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed: ' + error.message);
    }
  };

  // Handle print slip for a single row
  const handlePrintSlip = (rowData) => {
    if (!selectedTable || !selectedDatabase) {
      alert('No table selected');
      return;
    }

    // Build URL with row data
    let printUrl = `https://electroweighing.com/print-slip.php?database=${encodeURIComponent(selectedDatabase)}&table=${encodeURIComponent(selectedTable)}`;
    
    // Add all column data as parameters
    tableData.columns.forEach(col => {
      if (rowData[col] !== null && rowData[col] !== undefined) {
        printUrl += `&${encodeURIComponent(col)}=${encodeURIComponent(rowData[col])}`;
      }
    });

    // Add user details
    if (currentUser) {
      if (currentUser.name) {
        printUrl += `&userName=${encodeURIComponent(currentUser.name)}`;
      }
      if (currentUser.company) {
        printUrl += `&userCompany=${encodeURIComponent(currentUser.company)}`;
      }
    }

    console.log('Print Slip URL:', printUrl);

    // Open in new window to trigger download
    window.open(printUrl, '_blank');
  };

  // Filter and sort table rows
  const getFilteredAndSortedRows = () => {
    if (!tableData.rows || tableData.rows.length === 0) return [];

    let filteredRows = [...tableData.rows];

    // Find date columns (created_date, date, created_at, etc.)
    const dateColumns = tableData.columns.filter(col => {
      const colLower = col.toLowerCase();
      return colLower.includes('date') || colLower.includes('created') || colLower.includes('time');
    });

    // Apply date range filter (only on date columns)
    if ((fromDate || toDate) && dateColumns.length > 0) {
      filteredRows = filteredRows.filter(row => {
        return dateColumns.some(col => {
          const value = row[col];
          if (!value) return false;
          
          try {
            const rowDate = new Date(value);
            if (isNaN(rowDate.getTime())) return false;
            
            // Check from date
            if (fromDate) {
              const fromDateTime = new Date(fromDate);
              fromDateTime.setHours(0, 0, 0, 0);
              if (rowDate < fromDateTime) return false;
            }
            
            // Check to date
            if (toDate) {
              const toDateTime = new Date(toDate);
              toDateTime.setHours(23, 59, 59, 999);
              if (rowDate > toDateTime) return false;
            }
            
            return true;
          } catch (e) {
            return false;
          }
        });
      });
    }

    // Apply search filter (searches across all columns)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredRows = filteredRows.filter(row => {
        return tableData.columns.some(col => {
          const value = row[col];
          if (value == null) return false;
          return String(value).toLowerCase().includes(searchLower);
        });
      });
    }

    // Apply sorting
    if (sortColumn) {
      filteredRows.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        
        // Handle null/undefined
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        
        // Try numeric comparison first
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // Try date comparison
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
        }
        
        // String comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (sortOrder === 'asc') {
          return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        } else {
          return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        }
      });
    }

    return filteredRows;
  };

  // Format user info lines
  const nameLine = (() => {
    if (!currentUser) return '';
    const displayName = currentUser.name || currentUser.username || '';
    const uname = currentUser.username || '';
    const showParen = uname && displayName && uname.toLowerCase() !== displayName.toLowerCase();
    return displayName ? `${displayName}${showParen ? ` (${uname})` : ''}` : uname;
  })();

  const companyRoleLine = (() => {
    if (!currentUser) return '';
    const company = currentUser.company || '';
    const role = currentUser.role || '';
    return `${company}${(company && role) ? ' • ' : ''}${role}`;
  })();

  // Determine which company logo to display (by company/companyId)
  const companyLogo = (() => {
    if (!currentUser) return null;
    // Prefer companyId if available
    if (currentUser.companyId) {
      if (currentUser.companyId.toUpperCase() === 'COAD') {
        // Example: add more companyId/logo mappings here
        // return require('./IMG/COADLogo.png');
      }
    }
    if (currentUser.company) {
      const company = currentUser.company.toLowerCase();
      if (company.includes('lg') || company.includes('noida')) {
        return lgLogo;
      } else if (company.includes('electro') || company.includes('weighing')) {
        return ewLogo;
      }
      else if (company.includes('Raj') || company.includes('Sneh')) {
        return rajsnehagroLogo;
      }


      
    }
    return null;
  })();

  return (
    <main style={{ minHeight: '400px', padding: '40px 20px', background: '#fafafa' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* User Info Panel */}
        {currentUser && (
          <div style={{ background: '#fff', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              {/* Company Logo on the left */}
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', minWidth: '360px' }}>
                {companyLogo && (
                  <img src={companyLogo} alt="Company Logo" style={{ width: '360px', height: '80px', objectFit: 'contain' }} />
                )}
                {!companyLogo && <div style={{ width: '60px' }}></div>}
              </div>
              {/* User Info */}
              <div style={{ textAlign: 'right', flex: 1 }}>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Logged in as:</div>
                <div style={{ fontWeight: 600, color: '#0d47a1', fontSize: '1.1rem' }}>{nameLine}</div>
                {(currentUser.company || currentUser.role) && (
                  <div style={{ color: '#555', marginTop: '2px' }}>{companyRoleLine}</div>
                )}
                {currentUser.companyId && (
                  <div style={{ color: '#888', marginTop: '2px', fontSize: '0.9em' }}>Company ID: {currentUser.companyId}</div>
                )}
              </div>
              {/* User Avatar */}
              <img src={userImage} alt="User" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h2 style={{ color: '#333', margin: 0 }}>Dashboard</h2>
          </div>
          <button 
            onClick={() => setShowConnectForm(!showConnectForm)} 
            style={{ padding: '10px 20px', fontSize: '1rem', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            {showConnectForm ? 'Cancel' : 'Connect New Database'}
          </button>
        </div>
        
        {/* Custom Database Connection Form */}
        {showConnectForm && (
          <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
            <h3 style={{ marginTop: 0 }}>Connect to Custom Database</h3>
            <form onSubmit={handleConnectCustomDatabase} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <input
                type="text"
                placeholder="Database Host (e.g., srv1874.hstgr.io)"
                value={customDbConfig.host}
                onChange={e => setCustomDbConfig({ ...customDbConfig, host: e.target.value })}
                required
                style={{ padding: '10px', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Database Name"
                value={customDbConfig.dbname}
                onChange={e => setCustomDbConfig({ ...customDbConfig, dbname: e.target.value })}
                required
                style={{ padding: '10px', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Username"
                value={customDbConfig.username}
                onChange={e => setCustomDbConfig({ ...customDbConfig, username: e.target.value })}
                required
                style={{ padding: '10px', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="password"
                placeholder="Password"
                value={customDbConfig.password}
                onChange={e => setCustomDbConfig({ ...customDbConfig, password: e.target.value })}
                required
                style={{ padding: '10px', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <button type="submit" style={{ padding: '12px', fontSize: '1rem', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Connect
              </button>
            </form>
          </div>
        )}
        
        {stats.loading && <p>Loading dashboard data...</p>}
        
        {stats.error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            Error: {stats.error}
          </div>
        )}
        
        {!stats.loading && !stats.error && selectedDatabase && (
          <div>
            {/* Strictly filter tables by companyId for count and display */}
            {(() => {
              let filteredTables = [];
              if (stats.tables && currentUser && currentUser.companyId) {
                filteredTables = stats.tables.filter(table => table.name.toLowerCase().includes(currentUser.companyId.toLowerCase()));
              }
              return (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                  {/* Left: Total Weighbridge */}
                  <div style={{ background: '#fff', padding: '8px 16px', borderRadius: '6px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: '#1976d2', fontSize: '0.85rem', fontWeight: '600' }}>Total Weighbridge:</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{filteredTables.length}</span>
                  </div>
                </div>
              );
            })()}
            
            {/* Tables List (filtered by companyId on frontend for extra safety) */}
            {(() => {
              let filteredTables = [];
              if (stats.tables && currentUser && currentUser.companyId) {
                filteredTables = stats.tables.filter(table => table.name.toLowerCase().includes(currentUser.companyId.toLowerCase()));
              }
              return filteredTables.length > 0 ? (
                <div style={{ background: '#fff', padding: '8px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ color: '#333', marginTop: 0, marginBottom: '16px' }}>Tables</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#555' }}>Table Name</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#555' }}>Number of Rows</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTables.map((table, index) => (
                          <tr 
                            key={index} 
                            onClick={() => handleTableClick(table.name)}
                            style={{ 
                              borderBottom: '1px solid #eee', 
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '12px', color: '#1976d2', fontWeight: '500' }}>
                              📋 {table.name}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#1976d2', fontWeight: 'bold' }}>{table.rows.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null;
            })()}
            
            {/* No Tables Message */}
            {stats.tables && stats.tables.length === 0 && !stats.loading && (
              <div style={{ background: '#fff3cd', padding: '20px', borderRadius: '12px', border: '1px solid #ffc107', marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>⚠️ No Tables Found</h4>
                <p style={{ margin: 0, color: '#856404' }}>
                  The database "<strong>{selectedDatabase}</strong>" appears to have no tables, or you don't have permission to view them.
                </p>
                <p style={{ margin: '8px 0 0 0', color: '#856404' }}>
                  Please check:
                </p>
                <ul style={{ margin: '8px 0 0 0', color: '#856404' }}>
                  <li>Database contains tables on the server</li>
                  <li>Database user has SHOW TABLES permission</li>
                  <li>Try selecting a different database from the dropdown</li>
                </ul>
                <button 
                  onClick={() => fetchDashboardStats(selectedDatabase)}
                  style={{ marginTop: '12px', padding: '8px 16px', background: '#ffc107', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🔄 Retry
                </button>
              </div>
            )}
            
            {/* Loading State */}
            {stats.loading && (
              <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '12px', border: '1px solid #2196f3', marginTop: '16px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#1976d2' }}>⏳ Loading database information...</p>
              </div>
            )}
            
            {/* Error State */}
            {stats.error && (
              <div style={{ background: '#ffebee', padding: '20px', borderRadius: '12px', border: '1px solid #f44336', marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#c62828' }}>❌ Error Loading Database</h4>
                <p style={{ margin: 0, color: '#c62828' }}>{stats.error}</p>
                <button 
                  onClick={() => fetchDashboardStats(selectedDatabase)}
                  style={{ marginTop: '12px', padding: '8px 16px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🔄 Retry
                </button>
              </div>
            )}
          </div>
        )}

        
        {/* Inline Table Details Section (replaces modal) */}
        {selectedTable && (
          <section id="table-details" ref={tableDetailsRef} style={{ marginTop: '24px' }}>
            <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0 }}>Table: {selectedTable}</h3>
                  <p style={{ margin: '1px 0', color: '#666' }}>
                    {tableData.displayedRows || 0} of {tableData.totalRows || 0} rows
                    {tableData.totalRows > 1000 && <span style={{ color: '#d32f2f' }}> (Limited to first 1000 rows)</span>}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleExport('excel'); }}
                    style={{ padding: '8px 16px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    📊 Export Excel
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleExport('pdf'); }}
                    style={{ padding: '8px 16px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    📄 Export PDF
                  </button>
                  <button
                    onClick={closeTableView}
                    style={{ padding: '8px 16px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              {!tableData.loading && !tableData.error && tableData.columns.length > 0 && (
                <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #e0e0e0' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#555' }}>🔍 Filter & Sort Options</h4>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Sort By Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Sort By Column:</label>
                      <select 
                        value={sortColumn} 
                        onChange={(e) => setSortColumn(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', minWidth: '150px' }}
                      >
                        <option value="">-- No Sorting --</option>
                        {tableData.columns.map((col, idx) => (
                          <option key={idx} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>

                    {/* Sort Order */}
                    {sortColumn && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Order:</label>
                        <select 
                          value={sortOrder} 
                          onChange={(e) => setSortOrder(e.target.value)}
                          style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', minWidth: '100px' }}
                        >
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      </div>
                    )}

                    {/* Date Range Filter - From */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>From Date:</label>
                      <input 
                        type="date" 
                        value={fromDate} 
                        onChange={(e) => setFromDate(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                    </div>

                    {/* Date Range Filter - To */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>To Date:</label>
                      <input 
                        type="date" 
                        value={toDate} 
                        onChange={(e) => setToDate(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                    </div>

                    {/* Search Box */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexGrow: 1, minWidth: '200px' }}>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Search:</label>
                      <input 
                        type="text" 
                        value={searchText} 
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Search in all columns..."
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                    </div>

                    {/* Clear Filters Button */}
                    {(sortColumn || fromDate || toDate || searchText) && (
                      <button
                        onClick={() => { setSortColumn(''); setFromDate(''); setToDate(''); setSearchText(''); }}
                        style={{ padding: '6px 12px', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', marginTop: '18px' }}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              )}

              {tableData.loading && <p>Loading table data...</p>}
              {tableData.error && (
                <div style={{ background: '#ffebee', color: '#c62828', padding: '16px', borderRadius: '8px' }}>
                  Error: {tableData.error}
                </div>
              )}

              {!tableData.loading && !tableData.error && tableData.columns.length > 0 && (() => {
                const processedRows = getFilteredAndSortedRows();
                return (
                  <>
                    {/* Show filtered count if filters are active */}
                    {(sortColumn || fromDate || toDate || searchText) && (
                      <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#e3f2fd', borderRadius: '4px', fontSize: '13px', color: '#1976d2' }}>
                        📊 Showing {processedRows.length} of {tableData.rows.length} rows
                        {processedRows.length < tableData.rows.length && ' (filtered)'}
                      </div>
                    )}
                    <div style={{ overflow: 'auto', maxHeight: '65vh', border: '1px solid #eee', borderRadius: '8px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                            <th style={{ position: 'sticky', top: 0, background: '#f5f5f5', padding: '8px 12px', textAlign: 'center', fontWeight: 'bold', color: '#555', whiteSpace: 'nowrap', zIndex: 1, width: '80px' }}>
                              Action
                            </th>
                            {tableData.columns.map((col, index) => (
                              <th key={index} style={{ position: 'sticky', top: 0, background: '#f5f5f5', padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', color: '#555', whiteSpace: 'nowrap', zIndex: 1 }}>
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {processedRows.map((row, rowIndex) => (
                            <tr key={rowIndex} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => openSlipDetail(row)}>
                              <td style={{ padding: '1px 10px', textAlign: 'center' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handlePrintSlip(row); }}
                                  style={{ padding: '4px 8px', background: '#2196f3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                  title="Print Slip"
                                >
                                  🖨️ Print
                                </button>
                              </td>
                              {tableData.columns.map((col, colIndex) => (
                                <td key={colIndex} style={{ padding: '1px 10px', color: '#333', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {row[col] !== null ? String(row[col]) : <span style={{ color: '#999', fontStyle: 'italic' }}>NULL</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </section>
        )}

        {/* Slip Detail Pane */}
        {selectedTable && selectedRowId && (
          <section style={{ marginTop: '20px', background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #2196f3', paddingBottom: '12px' }}>
              <h2 style={{ fontSize: '20px', color: '#1976d2', margin: 0 }}>
                📋 Slip Details (ID: {selectedRowId})
              </h2>
              <button
                onClick={() => {
                  setSelectedRowId(null);
                  setSlipDetail({ data: null, images: [], loading: false, error: null });
                }}
                style={{ padding: '8px 16px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                ✖ Close
              </button>
            </div>

            {slipDetail.loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '18px', marginBottom: '8px' }}>⏳ Loading slip details...</div>
              </div>
            )}

            {slipDetail.error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '16px', borderRadius: '8px' }}>
                Error: {slipDetail.error}
              </div>
            )}

            {!slipDetail.loading && !slipDetail.error && slipDetail.data && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Left Column: Slip Data Fields */}
                <div>
                  <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
                    📄 Slip Information
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {Object.entries(slipDetail.data).map(([key, value], index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 'bold', color: '#555', width: '40%', background: '#f9f9f9' }}>
                            {key}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#333' }}>
                            {value !== null ? String(value) : <span style={{ color: '#999', fontStyle: 'italic' }}>NULL</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Right Column: Images */}
                <div>
                  <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
                    📷 Images ({slipDetail.images.length})
                  </h3>
                  {slipDetail.images.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999', background: '#f5f5f5', borderRadius: '8px' }}>
                      No images available for this slip
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {slipDetail.images.map((img, idx) => (
                      <div key={idx} style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#f9f9f9' }}>
                        <div style={{ padding: '8px', background: '#e3f2fd', fontSize: '12px', fontWeight: 'bold', color: '#1976d2' }}>
                          {img.field}
                        </div>
                        <div style={{ padding: '8px', textAlign: 'center' }}>
                          <img
                            src={img.url}
                            alt={img.field}
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => window.open(img.url, '_blank')}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none', color: '#999', padding: '20px', fontSize: '12px' }}>
                            ❌ Failed to load image
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

export default Dashboard;
